import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Shell components (P0-9 deliverable 10; amendments 2.1 section 7 "Shell and
 * icons"): Sidebar (280 px, resizable 240–360 by pointer and by arrow keys on
 * a `role="separator"` handle, collapsing to a 56 px rail), Header (48 px),
 * RightRail (320 px, collapsing automatically before the document column
 * would shrink below its measure), SplitView (420 px minimum per pane, tabs
 * below 1100 px) and the list rows of the sidebar.
 */
import { useEffect, useRef, useState, } from 'react';
import { TypeGlyph } from '../glyphs/type-glyph.js';
import { Icon } from '../icon/icon.js';
import { LAYOUT } from '../tokens/shared.js';
import { cx, useMediaQuery } from './infra.js';
import { IconButton } from './primitives.js';
/** A vertical `role="separator"` handle: drag it, or use the arrow keys, Home and End. */
export function SeparatorHandle({ label, value, min, max, onChange, step = 8, direction = 1, className, controls, }) {
    const drag = useRef(null);
    const clamp = (v) => Math.min(max, Math.max(min, Math.round(v)));
    const onPointerDown = (event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = { x: event.clientX, start: value };
    };
    const onPointerMove = (event) => {
        if (!drag.current)
            return;
        onChange(clamp(drag.current.start + direction * (event.clientX - drag.current.x)));
    };
    const onPointerUp = (event) => {
        drag.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };
    const onKeyDown = (event) => {
        const moves = {
            ArrowLeft: -step * direction,
            ArrowRight: step * direction,
            ArrowDown: -step * direction,
            ArrowUp: step * direction,
        };
        if (event.key in moves) {
            event.preventDefault();
            onChange(clamp(value + moves[event.key]));
        }
        else if (event.key === 'Home') {
            event.preventDefault();
            onChange(min);
        }
        else if (event.key === 'End') {
            event.preventDefault();
            onChange(max);
        }
    };
    return (_jsx("div", { role: "separator", "aria-orientation": "vertical", "aria-label": label, "aria-valuenow": value, "aria-valuemin": min, "aria-valuemax": max, "aria-controls": controls, tabIndex: 0, className: cx('cx-separator-handle', className), onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: onPointerUp, onPointerCancel: onPointerUp, onKeyDown: onKeyDown, children: _jsx("span", { className: "cx-separator-handle__grip", "data-control-boundary": "", "aria-hidden": "true" }) }));
}
export function Sidebar({ label, width, collapsed, onWidthChange, onCollapsedChange, collapseShortcut, header, children, id = 'cx-sidebar', }) {
    const shown = collapsed ? LAYOUT.rail : width;
    return (_jsxs("nav", { id: id, className: "cx-sidebar", "aria-label": label, "data-collapsed": collapsed ? '' : undefined, style: { width: shown, minWidth: shown }, children: [_jsxs("div", { className: "cx-sidebar__top", children: [header, _jsx(IconButton, { icon: "sidebar", label: `${collapsed ? 'Expand' : 'Collapse'} sidebar${collapseShortcut ? ` (${collapseShortcut})` : ''}`, pressed: !collapsed, onClick: () => onCollapsedChange(!collapsed), className: "cx-sidebar__toggle" })] }), _jsx("div", { className: "cx-sidebar__scroll", children: children }), !collapsed && (_jsx(SeparatorHandle, { label: "Resize sidebar", value: width, min: LAYOUT.sidebarMin, max: LAYOUT.sidebarMax, onChange: onWidthChange, controls: id, className: "cx-sidebar__handle" }))] }));
}
export function NavSection({ title, children, action, }) {
    return (_jsxs("section", { className: "cx-nav-section", "aria-label": title, children: [_jsxs("div", { className: "cx-nav-section__head", children: [_jsx("h2", { className: "cx-nav-section__title", children: title }), action] }), _jsx("ul", { className: "cx-nav-section__list", children: children })] }));
}
export function NavRow({ label, icon, glyph, count, active, depth = 0, onClick, countLabel, }) {
    return (_jsx("li", { className: "cx-nav-row-item", children: _jsxs("button", { type: "button", className: "cx-nav-row", "aria-current": active ? 'page' : undefined, "data-active": active ? '' : undefined, style: depth ? { paddingInlineStart: 12 + depth * 14 } : undefined, onClick: onClick, title: label, children: [glyph ? (_jsx(TypeGlyph, { type: glyph })) : icon ? (_jsx(Icon, { name: icon, weight: active ? 'fill' : 'regular' })) : (_jsx("span", { className: "cx-nav-row__spacer", "aria-hidden": "true" })), _jsx("span", { className: "cx-nav-row__label", children: label }), count !== undefined && (_jsxs("span", { className: "cx-nav-row__count", children: [count, countLabel && _jsxs("span", { className: "cx-visually-hidden", children: [" ", countLabel] })] }))] }) }));
}
/* ------------------------------------------------------------------ */
/* header and breadcrumbs                                              */
/* ------------------------------------------------------------------ */
export function Header({ start, children, end, }) {
    return (_jsxs("header", { className: "cx-header", children: [_jsx("div", { className: "cx-header__start", children: start }), _jsx("div", { className: "cx-header__center", children: children }), _jsx("div", { className: "cx-header__end", children: end })] }));
}
export function Breadcrumbs({ items }) {
    return (_jsx("nav", { className: "cx-breadcrumbs", "aria-label": "Breadcrumb", children: _jsx("ol", { children: items.map((item, i) => (_jsx("li", { "aria-current": i === items.length - 1 ? 'location' : undefined, children: item }, i))) }) }));
}
/* ------------------------------------------------------------------ */
/* right rail                                                          */
/* ------------------------------------------------------------------ */
/**
 * Whether the right rail must collapse: true when the region beside the
 * sidebar cannot hold the rail and the document column at its full measure
 * (72ch of the body face, measured on `measureRef`) plus its gutters.
 */
export function useRailAutoCollapse(regionRef, measureRef, { rail = LAYOUT.rightRail, gutters = 96 } = {}) {
    const [collapsed, setCollapsed] = useState(false);
    useEffect(() => {
        const region = regionRef.current;
        const measure = measureRef.current;
        if (!region || !measure)
            return;
        const check = () => {
            const available = region.getBoundingClientRect().width;
            const column = measure.getBoundingClientRect().width;
            setCollapsed(available - rail < column + gutters);
        };
        check();
        const observer = new ResizeObserver(check);
        observer.observe(region);
        observer.observe(measure);
        return () => observer.disconnect();
    }, [regionRef, measureRef, rail, gutters]);
    return collapsed;
}
export function RightRail({ label, open, autoCollapsed, children, id = 'cx-right-rail', }) {
    if (!open)
        return null;
    return (_jsx("aside", { id: id, className: "cx-right-rail", "aria-label": label, "data-overlay": autoCollapsed ? '' : undefined, children: children }));
}
/** Two panes side by side (420 px minimum each), or two tabs below 1100 px. */
export function SplitView({ panes, active, onActivate, firstWidth, onFirstWidthChange, }) {
    const wide = useMediaQuery(`(min-width: ${LAYOUT.splitTabsBelow}px)`);
    const ref = useRef(null);
    const [room, setRoom] = useState(0);
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const measure = () => setRoom(el.getBoundingClientRect().width);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    const fits = room === 0 || room >= LAYOUT.splitPaneMin * 2;
    if (!wide || !fits) {
        return (_jsxs("div", { ref: ref, className: "cx-split cx-split--tabs", children: [_jsx("div", { role: "tablist", "aria-label": "Split panes", className: "cx-split__tabs", children: panes.map((p, i) => (_jsx("button", { type: "button", role: "tab", "aria-selected": active === i, tabIndex: active === i ? 0 : -1, className: "cx-split__tab", onClick: () => onActivate(i), onKeyDown: (e) => {
                            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft')
                                onActivate(active === 0 ? 1 : 0);
                        }, children: p.label }, p.label))) }), _jsx("div", { role: "tabpanel", "aria-label": panes[active].label, className: "cx-split__panel", children: panes[active].content })] }));
    }
    const max = Math.max(LAYOUT.splitPaneMin, room - LAYOUT.splitPaneMin);
    const first = Math.min(max, Math.max(LAYOUT.splitPaneMin, firstWidth ?? Math.round(room / 2)));
    return (_jsx("div", { ref: ref, className: "cx-split", style: { gridTemplateColumns: `${first}px auto minmax(${LAYOUT.splitPaneMin}px, 1fr)` }, children: panes.map((p, i) => (_jsx(SplitPaneView, { pane: p, active: active === i, onActivate: () => onActivate(i), children: i === 1 && (_jsx(SeparatorHandle, { label: "Resize split panes", value: first, min: LAYOUT.splitPaneMin, max: max, onChange: (v) => onFirstWidthChange?.(v), className: "cx-split__handle" })) }, p.label))) }));
}
function SplitPaneView({ pane, active, onActivate, children, }) {
    return (_jsxs(_Fragment, { children: [children, _jsx("section", { className: "cx-split__pane", "aria-label": pane.label, "data-active": active ? '' : undefined, onFocusCapture: onActivate, onPointerDown: onActivate, children: pane.content })] }));
}
//# sourceMappingURL=shell.js.map